import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { ReactNode } from "react";

interface ChristmasThemeState {
  /** Campaña navideña activa (re-tematiza toda la app vía tokens). */
  enabled: boolean;
  /** Efecto de nieve. */
  snow: boolean;
  /** Micro-animaciones y elementos flotantes. */
  animations: boolean;
  setEnabled: (value: boolean) => void;
  setSnow: (value: boolean) => void;
  setAnimations: (value: boolean) => void;
  toggle: () => void;
}

const ChristmasThemeContext = createContext<ChristmasThemeState | undefined>(
  undefined,
);

const STORAGE_KEY = "vendeya-christmas-theme";

interface StoredState {
  enabled: boolean;
  snow: boolean;
  animations: boolean;
}

function readStored(): StoredState {
  if (typeof window === "undefined") {
    return { enabled: true, snow: true, animations: true };
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<StoredState>;
      return {
        enabled: parsed.enabled !== false,
        snow: parsed.snow !== false,
        animations: parsed.animations !== false,
      };
    }
  } catch {
    // ignorar errores de parseo y usar valores por defecto
  }
  return { enabled: true, snow: true, animations: true };
}

/**
 * Proveedor del tema navideño (campaña visual temporal).
 * Añade la clase "christmas" a <html> para redefinir los tokens de color,
 * y persiste las preferencias del usuario en localStorage.
 */
export function ChristmasThemeProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<StoredState>(readStored);

  useEffect(() => {
    const root = document.documentElement;
    if (state.enabled) {
      root.classList.add("christmas");
    } else {
      root.classList.remove("christmas");
    }
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // ignorar
    }
  }, [state]);

  const value = useMemo<ChristmasThemeState>(
    () => ({
      enabled: state.enabled,
      snow: state.snow,
      animations: state.animations,
      setEnabled: (enabled) => setState((s) => ({ ...s, enabled })),
      setSnow: (snow) => setState((s) => ({ ...s, snow })),
      setAnimations: (animations) => setState((s) => ({ ...s, animations })),
      toggle: () => setState((s) => ({ ...s, enabled: !s.enabled })),
    }),
    [state],
  );

  return (
    <ChristmasThemeContext.Provider value={value}>
      {children}
    </ChristmasThemeContext.Provider>
  );
}

export function useChristmasTheme(): ChristmasThemeState {
  const context = useContext(ChristmasThemeContext);
  if (context === undefined) {
    throw new Error(
      "useChristmasTheme debe usarse dentro de <ChristmasThemeProvider>",
    );
  }
  return context;
}